import { ApolloError } from '@apollo/client';
import {
  useCascade,
  UseCascadeResult,
  useMutation,
  useTranslation,
} from '@gimlite/common';
import { WizardItemType, Zone } from '@gimlite/ui-v1';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Mutation,
  MutationCreateContractArgs,
  MutationCreatePoolArgs,
} from '../../../../client/graphql';
import { createContractGQL } from '../../../../common/gql/createContract.gql';
import { createPoolGQL } from '../../../../common/gql/createPool.gql ';
import {
  convertCredentialsFrontToBack,
  CredentialType,
} from '../../../../common/mapper/credential.mapper';
import { ContractMasterPoolLine } from '../../../../composition/line/contract-master-pool.line';
import {
  ContractMotoristLine,
  statusHelper,
} from '../../../../composition/line/contract-motorist.line';
import { ContractWizardInfos } from '../../../../composition/wizard-infos/contract.wizard-infos';
import {
  SummaryForm,
  SummaryFormType,
} from '../../../../form/summary/summary.form';
import { CreateNewMasterPoolContractWizardContext } from '../../createNewMasterPoolContract.wizard';

type ResultPosibility = any;
//  Mutation['createPool']
// | Mutation['createMotorist']
// | Mutation['createLocalMotorist'];

export const SummaryStep = ({
  context: {
    parkingId,
    contractBase,
    product,
    contractMode,
    contracts,
    tenant,
    poolAdmin,
  },
  validate,
  submit,
}: WizardItemType.Config.Component<CreateNewMasterPoolContractWizardContext>) => {
  const { t, lang } = useTranslation();

  const [created, setCreated] = useState<SummaryFormType.Data.Line[]>();
  const [createdToExport, setCreatedExport] =
    useState<Array<SummaryFormType.Data.Created>>();

  const [createContractCall] = useMutation<
    { createContract: Mutation['createContract'] },
    MutationCreateContractArgs
  >(createContractGQL);

  const [createPoolCall] = useMutation<
    { createPool: Mutation['createPool'] },
    MutationCreatePoolArgs
  >(createPoolGQL);

  const { isFinish } = useCascade<ResultPosibility>([
    async () => {
      const _id = uuidv4();
      try {
        const response = await createPoolCall({
          variables: {
            input: {
              tenant: {
                name1: tenant.name1 as string,
                name2: tenant.name2,
                address1: tenant.address1,
                address2: tenant.address2,
                city: tenant.city,
                country: tenant.country,
                tenantCanCloseSession: null,
                tenantCanCreateSubContract: tenant.canCreateSubContract,
                zipcode: tenant.zipCode,
              },
              pool: {
                isEnabled: contractBase.state,
                parkingId,
                productId: product.id,
                spacesAllocated: contractBase.capacity,
                startDate: contractBase.startDate,
                stopDate: contractBase.endDate,
                reference: contractBase.reference,
                description: contractBase.description,
                articleCode: contractBase.articleCode!,
                antiPassBackMode: contractBase.APBMode,
              },
              user: {
                firstName: poolAdmin.firstName,
                lang: poolAdmin.lang,
                lastName: poolAdmin.lastName,
                username: poolAdmin.username,
                phone: poolAdmin.phone,
              },
            },
          },
        });

        if (
          response?.errors &&
          Array.isArray(response?.errors) &&
          response?.errors.length > 0
        ) {
          throw response?.errors[0]?.message;
        }

        const createPool = response?.data?.createPool;

        setCreated((list) => [
          ...(list ? list : []),
          {
            _id,
            items: {
              component: (
                <ContractMasterPoolLine
                  data={{
                    code: createPool?.ospContractId || null,
                    name1: createPool?.name1 || null,
                    name2: createPool?.name2 || null,
                    status: createPool?.isEnabled ? 'ACTIVE' : 'SUSPENDED',
                  }}
                  config={{ action: false }}
                />
              ),
              state: { type: 'success' },
            },
          },
        ]);

        return { title: 'CreatePool', response: createPool };
      } catch (error: unknown) {
        setCreated((list) => [
          ...(list ? list : []),
          {
            _id,
            items: {
              component: (
                <ContractMasterPoolLine
                  data={{
                    code: null,
                    name1: tenant.name1!, //! Les validateurs forcent la présence
                    name2: tenant.name2,
                    status: contractBase.state ? 'ACTIVE' : 'SUSPENDED', //! Il n'existe pas en response
                  }}
                  config={{ action: false }}
                />
              ),
              state: {
                type: 'error',
                message:
                  error instanceof ApolloError
                    ? `${error.message}`
                    : `${error}`,
              },
            },
          },
        ]);
        return {
          title: 'CreatePool',
          response: new Error(
            error instanceof ApolloError ? `${error.message}` : `${error}`,
          ),
        };
      }
    },
    ...(contractMode === 'create'
      ? contracts.created.flatMap((contract) => [
          async (
            prevResult: Array<UseCascadeResult<ResultPosibility>>,
            index: number,
          ) => {
            try {
              if (
                prevResult[0].response instanceof Error ||
                !('_id' in prevResult[0].response)
              )
                throw t('poolWasNotCreated');

              const response = await createContractCall({
                variables: {
                  input: {
                    parkingId: parkingId!,
                    productId: product.id!,
                    startDate: contractBase.startDate!,
                    stopDate: contractBase.endDate!,
                    description: contractBase.description,
                    reference: contractBase.reference,
                    antiPassBackMode: contractBase.APBMode,
                    isEnabled: contractBase.state,
                    name1: contract.firstName,
                    name2: contract.lastName,
                    lang: contract.lang,
                    username: contract.email,
                    sendInvitation: contract.sendInvitation,
                    articleCode: contractBase.articleCode!,
                    linkedContractId: prevResult[0].response.defaultPoolId,
                    freeFlagIsEnabled:
                      contractBase.APBMode !== 'NONE'
                        ? !contractBase.APBNext
                        : null,
                    credentials: convertCredentialsFrontToBack(
                      [
                        {
                          value: contract.visibleId,
                          description: contract.uid,
                          type: 'uid',
                        },
                        {
                          value: contract.plate,
                          description: null,
                          type: 'plate',
                        },
                      ].reduce(
                        (
                          acc: CredentialType.CredentialFromFront[],
                          { value, type, description },
                        ) => {
                          if (type === 'plate' && value) {
                            return [
                              ...acc,
                              {
                                name: 'PLATE',
                                plate: value.toUpperCase(),
                                description: '',
                              } as CredentialType.CredentialFromFront,
                            ];
                          } else if (type === 'uid' && value) {
                            return [
                              ...acc,
                              {
                                name: 'RFID',
                                visibleId: value,
                                uid: description,
                              } as CredentialType.CredentialFromFront,
                            ];
                          } else {
                            return acc;
                          }
                        },
                        [],
                      ),
                    ).map(({ _id, ...rest }) => ({
                      ...rest,
                    })),
                  },
                },
              });

              if (
                response?.errors &&
                Array.isArray(response?.errors) &&
                response?.errors.length > 0
              ) {
                throw response?.errors[0]?.message;
              }

              const createContract = response?.data?.createContract;

              setCreated((list) => [
                ...(list ? list : []),
                {
                  _id: uuidv4(),
                  items: {
                    component: (
                      <ContractMotoristLine
                        data={{
                          firstName: createContract?.name1 || null,
                          contractId: createContract?.ospContractId || null,
                          lastName: createContract?.name2 || null,
                          status: statusHelper({
                            isEnabled: createContract?.isEnabled!,
                            startDate: new Date(createContract?.startDate!),
                            stopDate: new Date(createContract?.stopDate!),
                          }),
                        }}
                        config={{ action: false }}
                      />
                    ),
                    state: { type: 'success' },
                  },
                },
              ]);

              return { title: 'CreateContract', response: createContract };
            } catch (error: unknown) {
              setCreated((list) => [
                ...(list ? list : []),
                {
                  _id: uuidv4(),
                  items: {
                    component: (
                      <ContractMotoristLine
                        data={{
                          firstName: contract.firstName,
                          contractId: null,
                          lastName: contract.lastName,
                          status: contractBase.state ? 'ACTIVE' : 'SUSPENDED',
                        }}
                        config={{ action: false }}
                      />
                    ),
                    state: {
                      type: 'error',
                      message:
                        error instanceof ApolloError
                          ? `${error.message}`
                          : `${error}`,
                    },
                  },
                },
              ]);

              return {
                title: 'CreateContract',
                response: new Error(
                  error instanceof ApolloError
                    ? `${error.message}`
                    : `${error}`,
                ),
              };
            }
          },
        ])
      : contracts.checked.map(
          (contract) =>
            async (prevResult: Array<UseCascadeResult<ResultPosibility>>) => {
              const _id = uuidv4();

              try {
                if (
                  prevResult[0].response instanceof Error ||
                  !('_id' in prevResult[0].response)
                ) {
                  throw t('poolWasNotCreated');
                }

                const response = await createContractCall({
                  variables: {
                    input: {
                      name1: contract.name1,
                      name2: contract.name2,
                      lang: contract.lang,
                      username: contract.username,
                      reference: contract.reference,
                      parkingId: parkingId!,
                      productId: product.id!,
                      startDate: contractBase.startDate!,
                      stopDate: contractBase.endDate!,
                      description: contractBase.description,
                      antiPassBackMode: contractBase.APBMode,
                      isEnabled: contractBase.state,
                      sendInvitation: !!contract.username,
                      motoristId: contract.motoristId,
                      address:{
                        address1:contract.address?.address1 || undefined,
                        address2:contract.address?.address2 || undefined,
                        zipcode:contract.address?.zipcode  || undefined,
                        city:contract.address?.city || undefined,
                        country:contract.address?.country || undefined,
                      },
                      shortUid: contract.shortUid,
                      articleCode: contractBase.articleCode!,
                      linkedContractId: prevResult[0].response.defaultPoolId,
                      credentials: contract.credentials?.map(
                        ({ __typename, _id, ...rest }) => rest,
                      ),
                    },
                  },
                });

                if (
                  response?.errors &&
                  Array.isArray(response?.errors) &&
                  response?.errors.length > 0
                ) {
                  throw response?.errors[0]?.message;
                }

                const createContract = response?.data?.createContract;

                setCreated((list) => [
                  ...(list ? list : []),
                  {
                    _id,
                    items: {
                      component: (
                        <ContractMotoristLine
                          data={{
                            firstName: createContract?.name1 || null,
                            contractId: createContract?.ospContractId || null,
                            lastName: createContract?.name2 || null,
                            status: statusHelper({
                              isEnabled: createContract?.isEnabled!,
                              startDate: new Date(createContract?.startDate!),
                              stopDate: new Date(createContract?.stopDate!),
                            }),
                          }}
                          config={{ action: false }}
                        />
                      ),
                      state: { type: 'success' },
                    },
                  },
                ]);

                return { title: 'CreateContract', response: createContract };
              } catch (error: unknown) {
                setCreated((list) => [
                  ...(list ? list : []),
                  {
                    _id,
                    items: {
                      component: (
                        <ContractMotoristLine
                          data={{
                            firstName: contract.name1 || null,
                            contractId: null,
                            lastName: contract.name2 || null,
                            status: contractBase.state ? 'ACTIVE' : 'SUSPENDED',
                          }}
                          config={{ action: false }}
                        />
                      ),
                      state: {
                        type: 'error',
                        message:
                          error instanceof ApolloError
                            ? `${error.message}`
                            : `${error}`,
                      },
                    },
                  },
                ]);

                return {
                  title: 'CreateContract',
                  response: new Error(
                    error instanceof ApolloError
                      ? `${error.message}`
                      : `${error}`,
                  ),
                };
              }
            },
        )),
  ]);

  useEffect(() => {
    validate(isFinish);
  }, [isFinish]);

  return (
    <Zone
      config={{
        gap: {
          y: 1,
          x: 1,
        },
        zones: [['info', 'list']],
        rows: ['1fr'],
        columns: ['min-content', '1fr'],
      }}
    >
      <Zone.Area config={{ area: 'info' }}>
        <ContractWizardInfos
          data={{
            productName: product.id ? t(`product-${product.id}-name`) : null,
            APBMode: contractBase.APBMode,
            APBNext:
              contractBase.APBMode !== 'NONE' ? contractBase.APBNext : null,
            state: contractBase.state,
            startDate: contractBase.startDate,
            endDate: contractBase.endDate,
            description: contractBase.description,
            reference: contractBase.reference,
            articleCode: contractBase.articleCode,
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'list' }}>
        <SummaryForm
          data={{
            list: created,
            created: createdToExport,
          }}
        />
      </Zone.Area>
    </Zone>
  );
};
